<template>
  <a-drawer
    :width="600"
    :title="null"
    placement="right"
    :open="visible"
    @close="handleClose"
    :closable="false"
    class="message-drawer"
  >
    <a-spin :spinning="messageLoading">
      <div class="message-panel">
        <!-- 消息列表视图 -->
        <div v-if="!showChatDetail" class="message-list-view">
          <!-- 头部 -->
          <div class="message-header">
            <h2 class="message-title">Messages</h2>
            <button class="close-btn" @click="handleClose">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <!-- 筛选标签 -->
          <div class="filter-tabs">
            <artemisads-segmented
              class="segmented"
              v-model:value="defaultFilter.associateEnable"
              @change="handleChangeAssociateEnable"
              :options="['All', 'Unread']"
            />
          </div>

          <!-- 消息列表 -->
          <div class="message-list" ref="messageListRef" @scroll="handleScroll">
            <a-spin :spinning="loading">
              <div v-if="filteredMessages.length === 0" class="empty-state">
                <a-empty />
              </div>
              <div v-else class="message-items">
                <div
                  v-for="message in filteredMessages"
                  :key="message.id"
                  class="message-item"
                  @click="handleMessageClick(message)"
                >
                  <div class="message-avatar">
                    <div class="default-avatar">
                      <img
                        :src="
                          message?.conversationPartner?.avatarUrl
                            ? message?.conversationPartner?.avatarUrl
                            : defaultAvatar
                        "
                        :alt="message?.conversationPartner?.userName"
                      />
                    </div>
                  </div>
                  <div class="message-content">
                    <div class="message-header-info">
                      <span class="sender-name">{{
                        message?.conversationPartner?.userName
                      }}</span>
                      <span class="message-time">{{
                        formatMessageTime(message?.latestMessage?.sentTime)
                      }}</span>
                    </div>
                    <p class="message-text">
                      {{ message?.latestMessage?.content }}
                    </p>
                  </div>
                  <div v-if="message.unreadCount > 0" class="unread-badge">
                    {{ message.unreadCount > 99 ? '99+' : message.unreadCount }}
                  </div>
                </div>
              </div>
            </a-spin>
          </div>
        </div>

        <!-- 聊天详情视图 -->
        <div v-else class="chat-detail-view">
          <!-- 聊天头部 -->
          <div class="chat-header">
            <button class="back-btn" @click="handleBackToList">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M12.5 15L7.5 10L12.5 5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div class="user-info">
              <div class="user-avatar">
                <img
                  :src="
                    currentChatUser.avatarUrl
                      ? currentChatUser.avatarUrl
                      : defaultAvatar
                  "
                  :alt="currentChatUser.userName"
                />
              </div>
              <span class="user-name">{{ currentChatUser.userName }}</span>
            </div>
            <button class="close-btn" @click="handleClose">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <!-- 聊天内容区域 -->
          <div
            class="chat-content"
            ref="chatContentRef"
            @scroll="handleChatScroll"
          >
            <div class="messages-container">
              <div
                v-for="message in chatMessages"
                :key="message.id"
                class="message-wrapper"
                :class="{ 'own-message': message.isOwn }"
              >
                <!-- 时间戳 -->
                <div class="message-time" v-if="message.showTime">
                  {{ formatMessageTime(message.createdAt) }}
                </div>

                <!-- 消息内容 -->
                <div class="message-bubble" :class="{ own: message.isOwn }">
                  <div class="message-avatar" v-if="!message.isOwn">
                    <img
                      :src="
                        currentChatUser.avatarUrl
                          ? currentChatUser.avatarUrl
                          : defaultAvatar
                      "
                      :alt="currentChatUser.userName"
                    />
                  </div>
                  <div class="message-text">{{ message.message }}</div>
                  <div class="message-avatar" v-if="message.isOwn">
                    <img
                      :src="
                        currentUser.avatarUrl
                          ? currentUser.avatarUrl
                          : defaultAvatar
                      "
                      :alt="currentUser.userName"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="chat-input">
            <div class="input-container">
              <a-textarea
                :value="newMessage"
                @input="newMessage = $event.target.value"
                placeholder="Please Input"
                :rows="3"
                class="message-input"
                @keypress="handleKeyPress"
                ref="messageInputRef"
                :maxlength="300"
                :bordered="false"
                :auto-size="{ minRows: 3, maxRows: 6 }"
              />
              <div class="send-btn-container">
                <!-- 调试信息 -->
                <div class="text-total-container">
                  (<span class="text-total">{{ newMessage.length }}</span> / 300
                  )
                </div>
                <artmisads-button
                  class="send-btn"
                  type="primary"
                  @click="handleSendMessage"
                  :disabled="!newMessage?.trim()"
                >
                  Send
                </artmisads-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-spin>
  </a-drawer>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { message } from 'ant-design-vue'
import { useMessageDrawerStore } from '../storeData/messageDrawer'
import { homeApi } from '../server/index'
import { useUserInfoStore } from '../storeData/index'
import moment from 'moment'
import defaultAvatar from '../assets/images/img/defaultAvatar.png'
const store = useUserInfoStore()
const userInfo = store.userInfo

const props = defineProps({
  // 是否加载中
  loading: {
    type: Boolean,
    default: false,
  },
})
const defaultFilter = ref({
  associateEnable: 'All',
})

const emit = defineEmits([
  'close',
  'markAsRead',
  'delete',
  'refresh',
  'messageClick',
  'scrollToBottom',
  'sendMessage',
])

// 时间格式化函数
const formatMessageTime = (timestamp) => {
  if (!timestamp) return ''

  const messageTime = moment(timestamp)
  const now = moment()
  const today = moment().startOf('day')
  const yesterday = moment().subtract(1, 'day').startOf('day')
  const dayBeforeYesterday = moment().subtract(2, 'day').startOf('day')
  const threeDaysAgo = moment().subtract(3, 'day').startOf('day')

  if (messageTime.isSame(today, 'day')) {
    // 今天显示时间
    return messageTime.format('HH:mm')
  } else if (messageTime.isSame(yesterday, 'day')) {
    // 昨天显示 Yesterday
    return 'Yesterday'
  } else if (messageTime.isSame(dayBeforeYesterday, 'day')) {
    // 前天显示 2 days ago
    return '2 days ago'
  } else if (messageTime.isSame(threeDaysAgo, 'day')) {
    // 大前天显示 3 days ago
    return '3 days ago'
  } else {
    // 更早的时间显示日期
    return messageTime.format('YYYY/MM/DD')
  }
}

// 计算消息的 showTime 属性
const calculateShowTime = (messages) => {
  if (!messages || messages.length === 0) return messages

  const processedMessages = [...messages]

  // 按时间排序（从早到晚）
  processedMessages.sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )

  // 初始化所有消息的 showTime 为 false
  processedMessages.forEach((msg) => {
    msg.showTime = false
  })

  // 第一个消息总是显示时间
  if (processedMessages.length > 0) {
    processedMessages[0].showTime = true
  }

  // 遍历消息，计算时间间隔
  for (let i = 1; i < processedMessages.length; i++) {
    const currentMessage = processedMessages[i]
    const previousMessage = processedMessages[i - 1]

    const currentTime = moment(currentMessage.createdAt)
    const previousTime = moment(previousMessage.createdAt)

    // 检查是否跨天
    const isDifferentDay = !currentTime.isSame(previousTime, 'day')

    // 检查时间间隔是否超过3小时
    const timeDiffInHours = currentTime.diff(previousTime, 'hours', true)
    const isMoreThan3Hours = timeDiffInHours >= 3

    // 如果跨天或间隔超过3小时，则显示时间
    if (isDifferentDay || isMoreThan3Hours) {
      currentMessage.showTime = true
    }
  }

  return processedMessages
}

// 全局状态管理
const messageDrawerStore = useMessageDrawerStore()

// 内部状态控制显示隐藏 - 使用全局状态
const visible = computed({
  get: () => messageDrawerStore.visible,
  set: (value) => messageDrawerStore.setVisible(value),
})
const activeFilter = ref('all')
const messageListRef = ref()
const receiverId = ref(null)
// 聊天详情相关状态
const showChatDetail = ref(false)
const currentChatUser = ref({})
const currentUser = ref({})
const chatMessages = ref([])
const newMessage = ref('')
const chatContentRef = ref()
const messageInputRef = ref()
const messages = ref([])
const total = ref(0)
const messageLoading = ref(false)
const param = {
  page: 1,
  size: 10000,
}
const filteredMessages = ref([])

// 定时器相关
const refreshTimer = ref(null)
const REFRESH_INTERVAL = 0.5 * 60 * 1000 // 2分钟
const isTimerActive = ref(false)
const handleChangeAssociateEnable = (value) => {
  activeFilter.value = value
  if (activeFilter.value === 'Unread') {
    filteredMessages.value = messages.value.filter(
      (message) => message.unreadCount > 0
    )
  } else {
    filteredMessages.value = messages.value
  }
}
// 处理关闭
const handleClose = () => {
  messageDrawerStore.hide()
  emit('close')
}
const getMessagesListSeller = async () => {
  const data = await homeApi.sellerMessagesList(param.value)

  if (data.success) {
    messages.value = data.data.data
    // 计算所有消息的unreadCount累加值
    total.value = data.data.data.reduce(
      (sum, message) => sum + (message.unreadCount || 0),
      0
    )
    filteredMessages.value = messages.value
  } else {
    message.error(data.error)
  }
  // homeApi.sellerMessagesList(param.value).then(res => {
  //   console.log(res,'seller信息列表')
  //   total.value = res.data?.total||0
  // })
}
const getMessagesListPublisher = async () => {
  const data = await homeApi.publisherMessagesList(param.value)
  if (data.success) {
    messages.value = data.data.data
    // 计算所有消息的unreadCount累加值
    total.value = data.data.data.reduce(
      (sum, message) => sum + (message.unreadCount || 0),
      0
    )
    filteredMessages.value = messages.value
  } else {
    message.error(data.error)
  }
  // homeApi.publisherMessagesList(param.value).then(res => {
  //   console.log(res,'publisher信息列表')
  //   total.value = res.data?.total||0
  // })
}
// 加载消息列表
const loadMessages = async () => {
  messageLoading.value = true
  if (userInfo.userType === 'publisher') {
    await getMessagesListPublisher()
  } else if (userInfo.userType === 'seller') {
    await getMessagesListSeller()
  }
  messageLoading.value = false
}
// 处理标记为已读
const handleMarkAsRead = (messageItem) => {
  emit('markAsRead', messageItem)
}

// 处理删除消息
const handleDelete = (messageItem) => {
  emit('delete', messageItem)
}

// 处理消息点击
const handleMessageClick = async (messageItem) => {
  // 设置当前聊天用户
  // console.log(messageItem,'messageItem')
  receiverId.value = messageItem?.conversationPartner?.userId

  // 切换到聊天详情视图
  showChatDetail.value = true

  // 加载消息
  await loadMessagesById(messageItem?.conversationPartner?.userId)

  // 聚焦到输入框并滚动到底部
  nextTick(() => {
    messageInputRef.value?.focus()
    scrollToBottomDelayed(200)
  })
}

// // 筛选后的消息列表
// const filteredMessages = computed(() => {
//   if (activeFilter.value === 'unread') {
//     return messages.value.filter(message => message.unreadCount > 0)
//   }
//   return messages.value
// })

// 处理滚动事件
const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target

  // 判断是否滚动到底部（允许5px的误差）
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5

  if (isAtBottom) {
    emit('scrollToBottom', {
      scrollTop,
      scrollHeight,
      clientHeight,
    })
  }
}

// 处理聊天滚动事件
const handleChatScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target

  // 判断是否滚动到底部
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5

  if (isAtBottom) {
    emit('scrollToBottom', {
      scrollTop,
      scrollHeight,
      clientHeight,
    })
  }
}
const getMessages = () => {}
// 返回消息列表
const handleBackToList = () => {
  showChatDetail.value = false
  newMessage.value = ''
  loadMessages()
}

// 处理发送消息
const handleSendMessage = async () => {
  if (!newMessage.value.trim()) return

  // 暂停定时器避免冲突
  pauseRefreshTimer()

  const messageData = {
    message: newMessage.value.trim(),
    receiverId: receiverId.value,
    messageType: 'text',
  }
  let res
  if (userInfo.userType === 'seller') {
    res = await homeApi.sellerMessagesSend(messageData)
  } else {
    res = await homeApi.publisherMessagesSend(messageData)
  }
  if (res.success) {
    newMessage.value = ''
    // 重新加载消息后滚动到底部
    await loadMessagesById(receiverId.value)
    // 等待消息加载完成后再滚动
    nextTick(() => {
      messageInputRef.value?.focus()
      scrollToBottomDelayed(300)
    })
  } else {
    message.error(res.error)
  }

  // 恢复定时器
  resumeRefreshTimer()
}

// 处理键盘事件
const handleKeyPress = (event) => {
  if (event.key === 'Enter') {
    handleSendMessage()
  }
}

// 滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (chatContentRef.value) {
      // 使用平滑滚动
      chatContentRef.value.scrollTo({
        top: chatContentRef.value.scrollHeight,
        behavior: 'smooth',
      })
    }
  })
}

// 强制滚动到底部（不使用平滑滚动）
const scrollToBottomImmediate = () => {
  nextTick(() => {
    if (chatContentRef.value) {
      chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight
    }
  })
}

// 延迟滚动到底部，确保DOM完全更新
const scrollToBottomDelayed = (delay = 100) => {
  setTimeout(() => {
    if (chatContentRef.value) {
      chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight
    }
  }, delay)
}

// 启动定时刷新
const startRefreshTimer = () => {
  // 如果抽屉未显示，不启动定时器
  if (!messageDrawerStore.visible) {
    return
  }

  // 清除现有定时器
  stopRefreshTimer()

  // console.log('启动定时刷新器，间隔:', REFRESH_INTERVAL / 1000, '秒')
  isTimerActive.value = true

  refreshTimer.value = setInterval(async () => {
    if (messageDrawerStore.visible && isTimerActive.value) {
      try {
        if (showChatDetail.value) {
          // 在聊天详情页面，刷新当前聊天消息
          if (receiverId.value) {
            // console.log('定时刷新聊天消息:', receiverId.value)
            await loadMessagesById(receiverId.value)
          }
        } else {
          // 在消息列表页面，刷新消息列表
          // console.log('定时刷新消息列表')
          await loadMessages()
        }
      } catch (error) {
        console.error('定时刷新消息失败:', error)
      }
    } else {
      // 如果抽屉关闭或定时器被禁用，停止定时器
      stopRefreshTimer()
    }
  }, REFRESH_INTERVAL)
}

// 停止定时刷新
const stopRefreshTimer = () => {
  if (refreshTimer.value) {
    // console.log('停止定时刷新器')
    clearInterval(refreshTimer.value)
    refreshTimer.value = null
    isTimerActive.value = false
  }
}

// 暂停定时刷新
const pauseRefreshTimer = () => {
  isTimerActive.value = false
  // console.log('暂停定时刷新器')
}

// 恢复定时刷新
const resumeRefreshTimer = () => {
  if (messageDrawerStore.visible) {
    isTimerActive.value = true
    // console.log('恢复定时刷新器')
  }
}
const sellerMessagesList = async (id) => {
  const data = await homeApi.sellerMessagesMsgDetail({
    ...param,
    conversationPartnerId: id,
  })

  if (data.success) {
    currentChatUser.value = data.data.conversationPartner
    currentUser.value = data.data.currentUser
    data?.data?.messages?.data?.map((item) => {
      item.isOwn = item?.sender?.userId === userInfo.delegatorUserId
    })
    // 应用 showTime 逻辑
    const processedMessages = calculateShowTime(data?.data?.messages?.data)
    chatMessages.value = processedMessages

    // 消息加载完成后延迟滚动到底部
    scrollToBottomDelayed(200)
  } else {
    message.error(data.error)
  }
}
const publisherMessagesList = async (id) => {
  const data = await homeApi.publisherMessagesMsgDetail({
    ...param,
    conversationPartnerId: id,
  })

  if (data.success) {
    currentChatUser.value = data.data.conversationPartner
    currentUser.value = data.data.currentUser
    data?.data?.messages?.data?.map((item) => {
      item.isOwn = item?.sender?.userId === userInfo.delegatorUserId
    })
    // 应用 showTime 逻辑
    const processedMessages = calculateShowTime(data?.data?.messages?.data)
    chatMessages.value = processedMessages

    // 消息加载完成后延迟滚动到底部
    scrollToBottomDelayed(200)
  } else {
    message.error(data.error)
  }
}
const loadMessagesById = async (id) => {
  messageLoading.value = true

  if (userInfo.userType === 'publisher') {
    await publisherMessagesList(id)
  } else if (userInfo.userType === 'seller') {
    await sellerMessagesList(id)
  }
  messageLoading.value = false
}
// 显示抽屉
const show = async (id) => {
  showChatDetail.value = false
  messageDrawerStore.show()
  activeFilter.value = 'all'
  await loadMessages()

  // 启动定时刷新
  startRefreshTimer()

  emit('refresh')
}

// 隐藏抽屉
const hide = () => {
  messageDrawerStore.hide()
  // 停止定时刷新
  stopRefreshTimer()
}

// 切换显示状态
const toggle = () => {
  messageDrawerStore.toggle()
  if (messageDrawerStore.visible) {
    // 启动定时刷新
    startRefreshTimer()
    emit('refresh')
  } else {
    // 停止定时刷新
    stopRefreshTimer()
  }
}

// 暴露方法给父组件使用
defineExpose({
  show,
  hide,
  toggle,
  startRefreshTimer,
  stopRefreshTimer,
  pauseRefreshTimer,
  resumeRefreshTimer,
  isTimerActive,
})

// 获取状态颜色
const getStatusColor = (status) => {
  const colorMap = {
    unread: 'red',
    read: 'green',
    urgent: 'orange',
    info: 'blue',
  }
  return colorMap[status] || 'default'
}

// 格式化时间
const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now - date

  if (diff < 60000) {
    // 1分钟内
    return 'Just now'
  } else if (diff < 3600000) {
    // 1小时内
    return `${Math.floor(diff / 60000)} minutes ago`
  } else if (diff < 86400000) {
    // 1天内
    return `${Math.floor(diff / 3600000)} hours ago`
  } else {
    return date.toLocaleDateString()
  }
}

// 监听visible变化，可以在这里添加刷新逻辑
watch(
  () => messageDrawerStore.visible,
  async (newVal) => {
    if (newVal) {
      // 启动定时刷新
      startRefreshTimer()
      emit('refresh')
    } else {
      // 停止定时刷新
      stopRefreshTimer()
    }
  }
)

// 监听参数变化
watch(
  () => messageDrawerStore.currentParam,
  (newParam) => {
    if (newParam && messageDrawerStore.visible) {
      // 当参数变化时，重新加载消息
      // console.log(newParam,'newParam')
      receiverId.value = newParam
      showChatDetail.value = true
      loadMessagesById(newParam)
    }
  }
)

// 监听聊天消息变化，自动滚动到底部
watch(
  () => chatMessages.value,
  () => {
    if (showChatDetail.value) {
      scrollToBottomDelayed(100)
    }
  },
  { deep: true }
)

// 监听showChatDetail变化，重新启动定时器以确保正确的刷新逻辑
watch(
  () => showChatDetail.value,
  () => {
    if (messageDrawerStore.visible) {
      // 重新启动定时器以应用新的刷新逻辑
      startRefreshTimer()
    }
  }
)

// 组件销毁时清理定时器
onUnmounted(() => {
  stopRefreshTimer()
})
</script>

<style lang="less" scoped>
.message-drawer {
  :deep(.ant-drawer-body) {
    padding: 16px !important;
  }
}

// 全局样式，确保抽屉样式生效
:global(.ant-drawer .ant-drawer-body) {
  padding: 16px !important;
}

// 更具体的选择器
:global(.ant-drawer-right .ant-drawer-body) {
  padding: 16px !important;
}
.send-btn-container {
  margin-top: 30px;
  margin-bottom: 10px;
  padding: 0 11px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .text-total-container {
    color: #0f062980;
    font-family: 'Sora';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;

    letter-spacing: 0.4px;
  }
  .text-total {
    color: #0f0629;
    font-family: 'Sora';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.4px;
  }
}
.message-panel {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;

  .message-title {
    color: #0f0629;
    font-family: 'Sora';
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: 0.5px;
    opacity: 0.9;
    margin: 0;
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: #666;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f5;
      color: #333;
    }
  }
}

.filter-tabs {
  display: flex;
  padding: 12px 16px;
  gap: 8px;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #999;

    .empty-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    p {
      margin: 0;
      font-size: 16px;
    }
  }
}

.message-items {
  .message-item {
    display: flex;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #fafafa;
    }

    &:last-child {
      border-bottom: none;
    }
  }
}

.message-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin-right: 16px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  :deep(img) {
    max-height: 56px;
    border-radius: 50%;
    object-fit: contain;
  }

  .default-avatar {
    width: 56px;
    height: 56px;
    background: #d9d9d9;
    border: 1px solid #d9d9d9;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 18px;
  }
}

.message-content {
  flex: 1;
  min-width: 0;

  .message-header-info {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 4px;

    .sender-name {
      color: #0f0629;
      font-family: 'Sora';
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      letter-spacing: 0.5px;
      opacity: 0.9;
    }

    .message-time {
      color: #0f062980;
      font-family: 'Sora';
      font-size: 14px;
      font-style: normal;
      font-weight: 400;

      letter-spacing: 0.5px;
      opacity: 0.9;
    }
  }

  .message-text {
    color: #0f0629b3;
    font-family: 'Sora';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0.5px;
    opacity: 0.9;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.unread-badge {
  background: #eb4e3d;
  color: white;
  border-radius: 20px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;

  font-family: 'Sora';
  font-size: 14.4px;
  font-style: normal;
  font-weight: 400;
  line-height: 14.4px;
  font-weight: 600;
  margin-left: 8px;
  flex-shrink: 0;
}

// 自定义滚动条
.message-list::-webkit-scrollbar {
  width: 4px;
}

.message-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.message-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.message-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 聊天详情样式 */
.chat-detail-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.chat-header {
  display: flex;
  align-items: center;
  padding: 20px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;

  .back-btn,
  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0 !important;
    border-radius: 4px;
    color: #666;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f5;
      color: #333;
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    flex: 1;
    margin: 0 10px;

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin-right: 10px;
      overflow: hidden;
      background: #d9d9d9;
      border: 1px solid #d9d9d9;
      display: flex;
      align-items: center;
      justify-content: center;
      :deep(img) {
        max-height: 32px;
        object-fit: contain;
      }

      .default-avatar {
        width: 100%;
        height: 100%;
        background: #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666;
        font-weight: 600;
        font-size: 16px;
      }
    }

    .user-name {
      font-family: 'Sora';
      font-size: 16px;
      font-weight: 600;
      color: #0f0629;
    }
  }
}

.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f7fc;
  .messages-container {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }
}

.message-wrapper {
  display: flex;
  flex-direction: column;

  &.own-message {
    align-items: flex-end;
  }

  .message-time {
    text-align: center;
    font-size: 12px;
    color: #999;
    margin-bottom: 8px;
    width: 100%;
  }

  .message-bubble {
    display: flex;
    align-items: flex-start;
    max-width: 100%;

    .message-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin-right: 16px;
      flex-shrink: 0;
      overflow: hidden;
      background: #d9d9d9;
      border: 1px solid #d9d9d9;
      display: flex;
      align-items: center;
      justify-content: center;
      :deep(img) {
        max-height: 32px;
        object-fit: cover;
      }

      .default-avatar {
        width: 100%;
        height: 100%;
        background: #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666;
        font-weight: 600;
        font-size: 12px;
      }
    }

    .message-text {
      padding: 6px 10px;

      border-radius: 8px;
      background: #fff;
      word-wrap: break-word;
      max-width: 82%;
      margin-right: 48px;
    }
    &.own .message-avatar {
      margin-right: 0px;
      margin-left: 16px;
    }
    &.own .message-text {
      background: linear-gradient(0deg, #8a64f426 0%, #8a64f426 100%), #fff;
      color: white;
      color: #0f0629b3;
      font-family: 'Sora';
      font-size: 14px;
      font-style: normal;
      font-weight: 400;

      letter-spacing: 0.5px;
      opacity: 0.9;
      margin-left: 48px;
      margin-right: 0px;
    }
  }
}

.chat-input {
  padding: 16px 5px;
  background: #fff;
  border-top: 1px solid #f0f0f0;

  .input-container {
    .message-input {
      flex: 1;
      font-family: 'Sora';
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 19px;
      letter-spacing: 0.5px;
      outline: none;
      transition: border-color 0.2s;

      &:focus {
        border-color: #6c5ce7;
      }

      &::placeholder {
        color: #999;
      }
    }
  }
}

// 聊天内容滚动条
.chat-content::-webkit-scrollbar {
  width: 4px;
}

.chat-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.chat-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.chat-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>

<style lang="less">
/* 全局样式，确保抽屉样式生效 */
.ant-drawer .ant-drawer-body {
  padding: 0 !important;
}

.ant-drawer-right .ant-drawer-body {
  padding: 0 !important;
}
</style>
